import { Component, ElementRef, forwardRef, inject, Input, OnInit, viewChild, ViewChild } from '@angular/core';
import { AbstractFormControl } from '../abstract-form-control';
import { SortDto, UserItemDto, UserService, } from '@artemis-software/wr-api';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  exhaustMap,
  Observable,
  of,
  startWith,
  Subject,
  tap,
} from 'rxjs';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { NbPopoverDirective } from '@nebular/theme';
import OrderEnum = SortDto.OrderEnum;

@Component({
  standalone: false,
  selector: 'wr-user-select',
  templateUrl: './user-select.component.html',
  styleUrls: ['./user-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserSelectComponent),
      multi: true,
    },
  ],
})
export class UserSelectComponent extends AbstractFormControl<UserItemDto> implements OnInit {

  readonly userService = inject(UserService);
  readonly router = inject(Router);

  popover = viewChild(NbPopoverDirective);

  @ViewChild('searchInput')
  input?: ElementRef;

  @Input()
  tenantIds$?: Observable<string[]>;

  filterText$ = new BehaviorSubject<string>('');

  nextPage = 0;

  trigger$ = new Subject<'reset' | 'filter' | 'loadMore'>();

  users$ = new BehaviorSubject<UserItemDto[]>([]);

  ngOnInit(): void {
    combineLatest([
      this.filterText$.pipe(debounceTime(750)),
      this.tenantIds$ ? this.tenantIds$.pipe(startWith(undefined)) : of(undefined),
      this.trigger$,
    ])
      .pipe(
        exhaustMap(([filterText, tenantIds, trigger]) => {
          let appliedFilterText: string | undefined = undefined;
          if (filterText?.length > 0) {
            appliedFilterText = filterText;
          }

          if (trigger === 'reset') {
            this.nextPage = 0;
          }

          return this.userService.findAll({
            tenantIds: tenantIds,
            fullName: appliedFilterText,
            page: this.nextPage,
            size: 10,
            sort: {
              sort: 'fullName',
              order: OrderEnum.Asc,
            },
          }).pipe(
            tap(users => {
              if (trigger === 'reset') {
                this.users$.next(users);
              } else {
                const current = this.users$.value || [];
                this.users$.next([...current, ...users]);
              }
              this.nextPage++;
            }),
          );
        })
      )
      .subscribe();
  }

  trackById(index: number, item: UserItemDto): string {
    return item.id;
  }

  override writeValue(user: UserItemDto): void {
    super.writeValue(user);
    this.filterText$.next(this.viewHandle(user));
  }

  onSelectionChange(user?: UserItemDto): void {
    if (user?.id) {
      this.filterText$.next(this.viewHandle(user));
      this.value = user;
      this.popover()?.hide();
    }
  }

  viewHandle(value?: UserItemDto | string): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value) {
      return value.firstName + ' ' + value.lastName;
    }
    return '';
  }

  clear(): void {
    this.value = undefined;
    this.filterText$.next('');
    this.trigger$.next('reset');
  }

  goToUser(): void {
    this.router.navigate(['user', this.value?.id]);
  }

  getTeamText(teams: string[]): string {
    return teams.join(', ');
  }
}
